import { Daycare } from '@/types/Daycare.ts'
import { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from '@/container/daycare/daycareProfile/meal/meal.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useTranslation } from 'react-i18next'
import { MealHabit } from '@/types/Meal.ts'
import { GET_DAYCARE_MEAL_HABITS } from '@/modules/API/meal/getMealDaycareSettings.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { Chip, ListItem } from '@mui/material'
import { Delete, Edit } from '@mui/icons-material'
import { useLazyQuery } from '@apollo/client'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import AddMealHabitDialog from '@/container/daycare/daycareProfile/meal/mealHabits/AddMealHabitDialog.tsx'
import UpdateMealHabitDialog from '@/container/daycare/daycareProfile/meal/mealHabits/UpdateMealHabitDialog.tsx'
import ConfirmDeleteMealHabitDialog from '@/container/daycare/daycareProfile/meal/mealHabits/ConfirmDeleteMealHabitDialog.tsx'

interface MealHabitsProps {
    daycare: Daycare
    canUpdate: boolean
}

const MealHabits: FunctionComponent<MealHabitsProps> = ({ daycare, canUpdate }: MealHabitsProps) => {
    const { t } = useTranslation()

    const [addMealHabitDialog, setAddMealHabitDialog] = useState<boolean>(false)
    const [editHabit, setEditHabit] = useState<MealHabit | null>(null)
    const [deleteHabit, setDeleteHabit] = useState<MealHabit | null>(null)

    const [habits, setHabits] = useState<MealHabit[]>([])

    const [getMealHabits, { loading, data, error, refetch }] = useLazyQuery(GET_DAYCARE_MEAL_HABITS, {
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (data) {
            setHabits(data.getDaycareMealHabits)
        }
    }, [data])

    useEffect(() => {
        getMealHabits()
    }, [daycare.id])

    return (
        <div className={styles.BlockPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myMealHabits')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddMealHabitDialog(true)}
                        />
                    )}
                </div>
                {loading && <Loading overall={false} />}
                {error && <ErrorMessage error={t('generalError')} />}
                {habits && habits.length > 0 ? (
                    <div className={styles.InputContainer}>
                        {[...habits]
                            .sort((a: MealHabit, b: MealHabit): number => a.habit.localeCompare(b.habit))
                            .map(
                                (data: MealHabit): JSX.Element => (
                                    <ListItem key={data.id} className={styles.ListItem}>
                                        <Chip
                                            className={styles.ChipDefault}
                                            label={
                                                <span className={styles.ChipLabel}>
                                                    <span>{data.habit}</span>
                                                    {canUpdate && (
                                                        <>
                                                            <Edit
                                                                className={styles.ChipActionIcon}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditHabit(data)
                                                                }}
                                                            />
                                                            <Delete
                                                                className={styles.ChipActionIcon}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setDeleteHabit(data)
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </span>
                                            }
                                        />
                                    </ListItem>
                                )
                            )}
                    </div>
                ) : (
                    <p>{t('noMealHabits')}</p>
                )}
            </div>
            <AddMealHabitDialog
                open={addMealHabitDialog}
                handleClose={() => setAddMealHabitDialog(false)}
                refetch={refetch}
                daycareId={daycare.id}
            />
            {editHabit && (
                <UpdateMealHabitDialog
                    open={!!editHabit}
                    handleClose={() => setEditHabit(null)}
                    refetch={refetch}
                    daycareId={daycare.id}
                    habit={editHabit}
                />
            )}
            {deleteHabit && (
                <ConfirmDeleteMealHabitDialog
                    open={!!deleteHabit}
                    handleClose={() => setDeleteHabit(null)}
                    daycareId={daycare.id}
                    habit={deleteHabit}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default MealHabits
